import {
  EmploymentType,
  Gender,
  WageType,
  WaitingPeriod,
} from '@zorro/clients';
import {
  RequiredClassCategories,
  VALIDATION_MESSAGES,
  parsePhoneNumber,
  waitingPeriodToNumberOfDays,
} from '@zorro/common-utils';
import {
  DateUtilInstance,
  earliestAllowedNonDependentBirthDate,
  getNow,
  latestAllowedNonDependentBirthDate,
  parseDate,
} from '@zorro/shared/formatters';
import * as yup from 'yup';

export const emailValidationSchema = yup
  .string()
  .email(VALIDATION_MESSAGES.emailValid)
  // eslint-disable-next-line require-unicode-regexp
  .matches(/^$|^[^\n\r@\u2028\u2029]*@[^\n\r.\u2028\u2029]*\..*$/, {
    message: VALIDATION_MESSAGES.emailValid,
  });

export const addEmployeeSchema = (
  waitingPeriod: WaitingPeriod | null,
  requiredClassCategories: RequiredClassCategories
) =>
  yup.object({
    idFromEmployer: yup.string().optional(),
    firstName: yup
      .string()
      .required(VALIDATION_MESSAGES.firstNameRequired)
      .min(2, VALIDATION_MESSAGES.firstNameMinTwoLetters),
    lastName: yup
      .string()
      .required(VALIDATION_MESSAGES.lastNameRequired)
      .min(2, VALIDATION_MESSAGES.lastNameMinTwoLetters),
    email: emailValidationSchema.required(VALIDATION_MESSAGES.emailRequired),
    personalEmail: emailValidationSchema.optional(),
    phone: yup
      .string()
      .optional()
      .nullable()
      /**
       * We expect a valid US phone number entered by the user
       * and react-phone-number-input automatically adds +1 prefix to it
       */
      .test('valid-phone', VALIDATION_MESSAGES.phoneNumberValid, (value) => {
        if (!value) return true;
        return parsePhoneNumber(value) !== undefined;
      }),
    residentialAddress: yup
      .string()
      .required(VALIDATION_MESSAGES.addressRequired),
    dateOfBirth: yup
      .date()
      .min(
        latestAllowedNonDependentBirthDate(),
        VALIDATION_MESSAGES.employeeMaxAgeValid
      )
      .max(
        earliestAllowedNonDependentBirthDate(),
        VALIDATION_MESSAGES.employeeMinAgeValid
      )
      .typeError(VALIDATION_MESSAGES.dateOfBirthRequired)
      .required(VALIDATION_MESSAGES.dateOfBirthRequired),
    gender: yup
      .mixed<Gender>()
      .oneOf(Object.values(Gender), VALIDATION_MESSAGES.genderRequired)
      .optional()
      .nullable(),
    class: yup.string().optional(),
    customClassCategory: requiredClassCategories.hasCustomClassCategory
      ? yup.string().required(VALIDATION_MESSAGES.customClassCategoryRequired)
      : yup.string().optional(),
    wageType: requiredClassCategories.hasWageType
      ? yup
          .mixed<WageType>()
          .required(VALIDATION_MESSAGES.wageTypeRequired)
          .oneOf(Object.values(WageType))
      : yup.mixed<WageType>().optional(),

    employmentType: requiredClassCategories.hasEmploymentType
      ? yup
          .mixed<EmploymentType>()
          .oneOf(Object.values(EmploymentType))
          .required(VALIDATION_MESSAGES.employmentTypeRequired)
      : yup
          .mixed<EmploymentType>()
          .oneOf(Object.values(EmploymentType))
          .optional(),
    salary: yup
      .number()
      .positive(VALIDATION_MESSAGES.yearlySalaryValid)
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .optional(),
    hireDate: yup.date().when('dateOfBirth', (dateOfBirth) => {
      if (dateOfBirth[0]) {
        return yup
          .date()
          .min(dateOfBirth, VALIDATION_MESSAGES.hireDateValid)
          .typeError(VALIDATION_MESSAGES.hireDateRequired)
          .required(VALIDATION_MESSAGES.hireDateRequired);
      }
      return yup
        .date()
        .min(
          latestAllowedNonDependentBirthDate(),
          VALIDATION_MESSAGES.hireDateValid
        )
        .typeError(VALIDATION_MESSAGES.hireDateRequired)
        .required(VALIDATION_MESSAGES.hireDateRequired);
    }),
    sendActivationEmail: yup.boolean().optional().default(true),
    eligibleFrom: yup.date().when('dateOfBirth', (dateOfBirth) => {
      let dateSchema = yup.date();
      dateSchema = dateOfBirth[0]
        ? dateSchema.min(dateOfBirth, VALIDATION_MESSAGES.eligibleFromValid)
        : dateSchema.min(
            latestAllowedNonDependentBirthDate(),
            VALIDATION_MESSAGES.eligibleFromValid
          );
      if (!waitingPeriod) {
        dateSchema = dateSchema
          .typeError(VALIDATION_MESSAGES.eligibleFromRequired)
          .required(VALIDATION_MESSAGES.eligibleFromRequired);
      }
      return dateSchema;
    }),
    ssn: yup.string().optional(),
    fipsCode: yup.string().optional(),
    subEntity2Id: yup.string().optional(),
    subEntityId: yup.string().optional(),
  });

export type CreateEmployeeFormFields = yup.InferType<
  ReturnType<typeof addEmployeeSchema>
>;

export const calculateEligibleFrom = (
  formHireDate?: Date,
  waitingPeriod?: WaitingPeriod | null
): DateUtilInstance => {
  if (formHireDate instanceof Date) {
    const parsedHireDate = parseDate(formHireDate);

    const eligibilityWithWaitingPeriod = waitingPeriod
      ? parsedHireDate.add(waitingPeriodToNumberOfDays[waitingPeriod], 'days')
      : parsedHireDate;

    const isFirstOfMonth = eligibilityWithWaitingPeriod.date() === 1;
    const eligibleFrom = isFirstOfMonth
      ? eligibilityWithWaitingPeriod
      : eligibilityWithWaitingPeriod.startOf('month').add(1, 'month');
    if (eligibleFrom.isValid()) {
      return eligibleFrom;
    }
  }
  return getNow();
};
